import { useState } from "react";
import { Code2, SquareArrowOutUpRight, Check, Copy } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScriptEmbedPreview } from "./ScriptEmbedPreview";

type EmbedSize = "compact" | "standard" | "large" | "custom";
type EmbedTheme = "dark" | "light";

interface EmbedConfig {
	size: EmbedSize;
	theme: EmbedTheme;
	width: number;
	height: number;
}

interface EmbedBuilderProps {
	station?: string;
}

export function EmbedBuilder({
	station = "macrowave/demo",
}: EmbedBuilderProps) {
	const [config, setConfig] = useState<EmbedConfig>({
		size: "standard",
		theme: "dark",
		width: 400,
		height: 250,
	});

	const [copied, setCopied] = useState<string | null>(null);

	// Script tag embedding code
	const scriptTagCode = `<script src="https://cdn.jsdelivr.net/npm/@macrowave/player@latest"></script>
<macrowave-player
  station="${station}"
  theme="${config.theme}">
</macrowave-player>`;

	// Iframe embedding code
	const embedUrl =
		typeof window !== "undefined"
			? `${window.location.origin}/embed/${station}?theme=${config.theme}`
			: `https://macrowave.co/embed/${station}?theme=${config.theme}`;

	const embedCode = `<iframe
  src="${embedUrl}"
  width="${config.width}"
  height="${config.height}"
  frameborder="0"
  allow="autoplay">
</iframe>`;

	const responsiveEmbedCode = `<div style="position: relative; padding-bottom: ${((config.height / config.width) * 100).toFixed(1)}%; height: 0; overflow: hidden; max-width: 100%;">
  <iframe
    src="${embedUrl}"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
    frameborder="0"
    allow="autoplay">
  </iframe>
</div>`;

	const handleCopy = async (code: string, id: string) => {
		await navigator.clipboard.writeText(code);
		setCopied(id);
		setTimeout(() => setCopied(null), 2000);
	};

	const sizePresets = {
		compact: { width: 350, height: 200 },
		standard: { width: 400, height: 250 },
		large: { width: 500, height: 300 },
	};

	const handleSizeChange = (size: EmbedSize) => {
		if (size !== "custom") {
			setConfig({
				...config,
				size,
				...sizePresets[size],
			});
		} else {
			setConfig({ ...config, size });
		}
	};

	return (
		<Tabs defaultValue="script" className="w-full">
			<TabsList className="mb-8">
				<TabsTrigger value="script" className="gap-2">
					<Code2 className="h-4 w-4" />
					Script Tag
					<span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">
						Recommended
					</span>
				</TabsTrigger>
				<TabsTrigger value="iframe" className="gap-2">
					<SquareArrowOutUpRight className="h-4 w-4" />
					Iframe
				</TabsTrigger>
			</TabsList>

			{/* Script Tag Tab */}
			<TabsContent value="script">
				<div className="grid grid-cols-1 gap-8 ">
					{/* Left Column - Configuration */}
					<div className="space-y-6">
						{/* Theme Selection */}
						<div className="rounded-lg border border-gray-200 bg-white p-6">
							<h2 className="mb-4 font-serif-600 text-lg">Theme</h2>
							<div className="space-y-3">
								<label className="flex cursor-pointer items-center gap-3">
									<input
										type="radio"
										name="theme-script"
										value="dark"
										checked={config.theme === "dark"}
										onChange={() => setConfig({ ...config, theme: "dark" })}
										className="h-4 w-4 border-gray-300 text-black focus:ring-black"
									/>
									<span className="font-serif text-gray-700">Dark</span>
								</label>
								<label className="flex cursor-pointer items-center gap-3">
									<input
										type="radio"
										name="theme-script"
										value="light"
										checked={config.theme === "light"}
										onChange={() => setConfig({ ...config, theme: "light" })}
										className="h-4 w-4 border-gray-300 text-black focus:ring-black"
									/>
									<span className="font-serif text-gray-700">Light</span>
								</label>
							</div>
						</div>

						{/* Script Tag Code */}
						<div className="rounded-lg border border-gray-200 bg-white p-6">
							<div className="mb-3 flex items-center justify-between">
								<h2 className="font-serif-600 text-lg">Embed Code</h2>
								<button
									onClick={() => handleCopy(scriptTagCode, "script")}
									className="flex items-center gap-2 rounded-md bg-black px-3 py-1 font-serif text-sm text-white transition-colors hover:bg-gray-800"
								>
									{copied === "script" ? (
										<>
											<Check className="h-4 w-4" />
											Copied!
										</>
									) : (
										<>
											<Copy className="h-4 w-4" />
											Copy
										</>
									)}
								</button>
							</div>
							<div
								onClick={() => handleCopy(scriptTagCode, "script")}
								className="cursor-pointer overflow-hidden rounded-md border border-gray-200 bg-gray-900 transition-all hover:border-gray-400"
							>
								<pre className="overflow-x-auto p-4">
									<code className="font-mono text-sm leading-relaxed">
										<span className="text-purple-400">&lt;script</span>{" "}
										<span className="text-blue-300">src</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">
											"https://cdn.jsdelivr.net/npm/@macrowave/player@latest"
										</span>
										<span className="text-purple-400">&gt;&lt;/script&gt;</span>
										{"\n"}
										<span className="text-purple-400">
											&lt;macrowave-player
										</span>
										{"\n  "}
										<span className="text-blue-300">station</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"{station}"</span>
										{"\n  "}
										<span className="text-blue-300">theme</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"{config.theme}"</span>
										<span className="text-purple-400">&gt;</span>
										{"\n"}
										<span className="text-purple-400">
											&lt;/macrowave-player&gt;
										</span>
									</code>
								</pre>
							</div>
							<p className="mt-3 font-serif text-sm text-gray-600">
								Add this code to your HTML where you want the player to appear.
								The player is fully responsive and will adapt to its container.
							</p>
						</div>
					</div>

					{/* Right Column - Live Preview */}
					<div className="h-fit lg:sticky lg:top-6">
						<div className="rounded-lg border border-gray-200 bg-white p-6">
							<h2 className="mb-4 font-serif-600 text-lg">Live Preview</h2>
							<div className="flex justify-center rounded-lg bg-gray-100 p-6">
								<ScriptEmbedPreview station={station} theme={config.theme} />
							</div>
							<p className="mt-4 text-center font-serif text-sm text-gray-600">
								This is how your embed will look on other websites
							</p>
						</div>
					</div>
				</div>
			</TabsContent>

			{/* Iframe Tab */}
			<TabsContent value="iframe">
				<div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.5fr_1fr]">
					{/* Left Column - Configuration */}
					<div className="space-y-6">
						{/* Size Selection */}
						<div className="rounded-lg border border-gray-200 bg-white p-6">
							<h2 className="mb-4 font-serif-600 text-lg">Size</h2>
							<div className="space-y-3">
								{(
									["compact", "standard", "large", "custom"] as EmbedSize[]
								).map((size) => (
									<label
										key={size}
										className="flex cursor-pointer items-center gap-3"
									>
										<input
											type="radio"
											name="size"
											value={size}
											checked={config.size === size}
											onChange={() => handleSizeChange(size)}
											className="h-4 w-4 border-gray-300 text-black focus:ring-black"
										/>
										<span className="font-serif text-gray-700 capitalize">
											{size}
											{size !== "custom" && (
												<span className="ml-2 text-sm text-gray-500">
													({sizePresets[size].width} ×{" "}
													{sizePresets[size].height})
												</span>
											)}
										</span>
									</label>
								))}
							</div>

							{/* Custom Size Inputs */}
							{config.size === "custom" && (
								<div className="mt-4 grid grid-cols-2 gap-4">
									<div>
										<label className="mb-1 block font-serif text-sm text-gray-600">
											Width (px)
										</label>
										<input
											type="number"
											value={config.width}
											onChange={(e) =>
												setConfig({
													...config,
													width: parseInt(e.target.value) || 400,
												})
											}
											className="w-full rounded-md border border-gray-300 px-3 py-2 font-serif"
											min="200"
											max="800"
										/>
									</div>
									<div>
										<label className="mb-1 block font-serif text-sm text-gray-600">
											Height (px)
										</label>
										<input
											type="number"
											value={config.height}
											onChange={(e) =>
												setConfig({
													...config,
													height: parseInt(e.target.value) || 250,
												})
											}
											className="w-full rounded-md border border-gray-300 px-3 py-2 font-serif"
											min="150"
											max="600"
										/>
									</div>
								</div>
							)}
						</div>

						{/* Theme Selection */}
						<div className="rounded-lg border border-gray-200 bg-white p-6">
							<h2 className="mb-4 font-serif-600 text-lg">Theme</h2>
							<div className="space-y-3">
								<label className="flex cursor-pointer items-center gap-3">
									<input
										type="radio"
										name="theme-iframe"
										value="dark"
										checked={config.theme === "dark"}
										onChange={() => setConfig({ ...config, theme: "dark" })}
										className="h-4 w-4 border-gray-300 text-black focus:ring-black"
									/>
									<span className="font-serif text-gray-700">Dark</span>
								</label>
								<label className="flex cursor-pointer items-center gap-3">
									<input
										type="radio"
										name="theme-iframe"
										value="light"
										checked={config.theme === "light"}
										onChange={() => setConfig({ ...config, theme: "light" })}
										className="h-4 w-4 border-gray-300 text-black focus:ring-black"
									/>
									<span className="font-serif text-gray-700">Light</span>
								</label>
							</div>
						</div>

						{/* Fixed Size Code */}
						<div className="rounded-lg border border-gray-200 bg-white p-6">
							<div className="mb-3 flex items-center justify-between">
								<h2 className="font-serif-600 text-lg">Fixed Size Code</h2>
								<button
									onClick={() => handleCopy(embedCode, "fixed")}
									className="flex items-center gap-2 rounded-md bg-black px-3 py-1 font-serif text-sm text-white transition-colors hover:bg-gray-800"
								>
									{copied === "fixed" ? (
										<>
											<Check className="h-4 w-4" />
											Copied!
										</>
									) : (
										<>
											<Copy className="h-4 w-4" />
											Copy
										</>
									)}
								</button>
							</div>
							<div
								onClick={() => handleCopy(embedCode, "fixed")}
								className="cursor-pointer overflow-hidden rounded-md border border-gray-200 bg-gray-900 transition-all hover:border-gray-400"
							>
								<pre className="overflow-x-auto p-4">
									<code className="font-mono text-sm leading-relaxed">
										<span className="text-purple-400">&lt;iframe</span>
										{"\n  "}
										<span className="text-blue-300">src</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"{embedUrl}"</span>
										{"\n  "}
										<span className="text-blue-300">width</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"{config.width}"</span>
										{"\n  "}
										<span className="text-blue-300">height</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"{config.height}"</span>
										{"\n  "}
										<span className="text-blue-300">frameborder</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"0"</span>
										{"\n  "}
										<span className="text-blue-300">allow</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"autoplay"</span>
										<span className="text-purple-400">&gt;</span>
										{"\n"}
										<span className="text-purple-400">&lt;/iframe&gt;</span>
									</code>
								</pre>
							</div>
						</div>

						{/* Responsive Code */}
						<div className="rounded-lg border border-gray-200 bg-white p-6">
							<div className="mb-3 flex items-center justify-between">
								<h2 className="font-serif-600 text-lg">Responsive Code</h2>
								<button
									onClick={() => handleCopy(responsiveEmbedCode, "responsive")}
									className="flex items-center gap-2 rounded-md bg-black px-3 py-1 font-serif text-sm text-white transition-colors hover:bg-gray-800"
								>
									{copied === "responsive" ? (
										<>
											<Check className="h-4 w-4" />
											Copied!
										</>
									) : (
										<>
											<Copy className="h-4 w-4" />
											Copy
										</>
									)}
								</button>
							</div>
							<div
								onClick={() => handleCopy(responsiveEmbedCode, "responsive")}
								className="cursor-pointer overflow-hidden rounded-md border border-gray-200 bg-gray-900 transition-all hover:border-gray-400"
							>
								<pre className="overflow-x-auto p-4">
									<code className="font-mono text-sm leading-relaxed">
										<span className="text-purple-400">&lt;div</span>{" "}
										<span className="text-blue-300">style</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">
											"position: relative; padding-bottom:{" "}
											{((config.height / config.width) * 100).toFixed(1)}%;
											height: 0; ..."
										</span>
										<span className="text-purple-400">&gt;</span>
										{"\n  "}
										<span className="text-purple-400">&lt;iframe</span>
										{"\n    "}
										<span className="text-blue-300">src</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"{embedUrl}"</span>
										{"\n    "}
										<span className="text-blue-300">style</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">
											"position: absolute; top: 0; left: 0; ..."
										</span>
										{"\n    "}
										<span className="text-blue-300">frameborder</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"0"</span>
										{"\n    "}
										<span className="text-blue-300">allow</span>
										<span className="text-gray-400">=</span>
										<span className="text-green-300">"autoplay"</span>
										<span className="text-purple-400">&gt;</span>
										{"\n  "}
										<span className="text-purple-400">&lt;/iframe&gt;</span>
										{"\n"}
										<span className="text-purple-400">&lt;/div&gt;</span>
									</code>
								</pre>
							</div>
						</div>
					</div>

					{/* Right Column - Live Preview */}
					<div className="h-fit lg:sticky lg:top-6">
						<div className="rounded-lg border border-gray-200 bg-white p-6">
							<h2 className="mb-4 font-serif-600 text-lg">Live Preview</h2>
							<div className="flex justify-center rounded-lg bg-gray-100 p-6">
								<iframe
									src={embedUrl}
									width={config.width}
									height={config.height}
									frameBorder="0"
									allow="autoplay"
									className="rounded-md border border-gray-300 shadow-lg"
									title="Embed Preview"
								/>
							</div>
							<p className="mt-4 text-center font-serif text-sm text-gray-600">
								This is how your embed will look on other websites
							</p>
						</div>
					</div>
				</div>
			</TabsContent>
		</Tabs>
	);
}
